/** Staff — Website Trade Enquiries: submissions from the public website trade-account + training forms. */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ContactsTab } from '@/components/contacts/ContactsTab';
import { ArrowLeft, Home, Inbox, Loader2, Mail, Phone, Search, UserPlus, CheckCircle2 } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const STAFF = ['admin', 'manager', 'team'];
const STATUS = [['new', 'New', 'bg-sky-100 text-sky-800'], ['contacted', 'Contacted', 'bg-amber-100 text-amber-800'], ['account_created', 'Account created', 'bg-emerald-100 text-emerald-800'], ['done', 'Done', 'bg-gray-100 text-gray-700'], ['spam', 'Spam', 'bg-rose-100 text-rose-800']];
const STATUS_CLS = Object.fromEntries(STATUS.map(([k, , c]) => [k, c]));
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

function EnquiryCard({ e, onPatch }) {
  const [notes, setNotes] = useState(e.notes || '');
  const navigate = useNavigate();
  const createAccount = () => {
    const qs = new URLSearchParams({ create_account: '1', email: e.email || '', name: e.name || '', company: e.company || '', phone: e.phone || '' });
    navigate(`/staff?${qs}`);
  };
  return (
    <Card className="p-4" data-testid={`enquiry-card-${e.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-bold text-gray-900">{e.name}</h3>
            {e.company && <span className="text-sm text-gray-600">· {e.company}</span>}
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{e.path_label}</span>
            {e.service && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{e.service}</span>}
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_CLS[e.status] || STATUS_CLS.new}`} data-testid={`enquiry-status-${e.id}`}>{(STATUS.find(([k]) => k === e.status) || STATUS[0])[1]}</span>
          </div>
          <div className="text-xs text-gray-600 mt-1 flex flex-wrap gap-3">
            {e.email && <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1 hover:underline"><Mail className="w-3 h-3" />{e.email}</a>}
            {e.phone && <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1 hover:underline"><Phone className="w-3 h-3" />{e.phone}</a>}
            {e.postcode && <span>{e.postcode}</span>}
            <span className="text-gray-400">Ref {e.id} · {fmt(e.created_at)}</span>
          </div>
          {e.message && <p className="text-sm text-gray-700 bg-gray-50 rounded p-2 mt-2 whitespace-pre-wrap">{e.message}</p>}
          {e.existing_user && <p className="text-[11px] text-emerald-700 mt-1 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Account exists: {e.existing_user.name} ({e.existing_user.role}) — <Link to={`/staff/installers/${e.existing_user.user_id}`} className="underline">open profile</Link></p>}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <select value={e.status || 'new'} onChange={(ev) => onPatch(e.id, { status: ev.target.value })} className="h-8 text-xs border border-gray-200 rounded-md px-2 bg-white" data-testid={`enquiry-status-select-${e.id}`}>
            {STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {!e.existing_user && e.path === 'installer_trade' && (
            <button type="button" onClick={createAccount} className="inline-flex items-center gap-1 text-xs font-bold text-white bg-[#7A0B7A] hover:bg-[#5a085a] rounded-md px-3 py-1.5" data-testid={`enquiry-create-account-${e.id}`}>
              <UserPlus className="w-3.5 h-3.5" /> Create installer account
            </button>
          )}
        </div>
      </div>
      <textarea value={notes} onChange={(ev) => setNotes(ev.target.value)} onBlur={() => notes !== (e.notes || '') && onPatch(e.id, { notes })} rows={1} placeholder="Staff notes — saves when you click away"
        className="mt-3 w-full text-xs border border-dashed border-gray-200 rounded-md px-2 py-1.5 bg-white focus:border-gray-400 focus:outline-none" data-testid={`enquiry-notes-${e.id}`} />
      {e.updated_by_name && <p className="text-[10px] text-gray-400 mt-1">Last updated by {e.updated_by_name} · {fmt(e.updated_at)}</p>}
    </Card>
  );
}

export default function WebsiteEnquiries() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') || 'contacts';
  const [path, setPath] = useState('installer_trade');
  const [status, setStatus] = useState('new');
  const [q, setQ] = useState('');
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => { if (user && !STAFF.includes(user.role)) navigate('/', { replace: true }); }, [user, navigate]);

  const load = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ path, status, q });
      const [r1, r2] = await Promise.all([fetch(`${API_URL}/api/staff/website-enquiries?${qs}`, { credentials: 'include' }), fetch(`${API_URL}/api/staff/website-enquiries/summary`, { credentials: 'include' })]);
      if (!r1.ok) throw new Error('Could not load enquiries');
      setItems((await r1.json()).items);
      if (r2.ok) setSummary(await r2.json());
    } catch (e) { toast.error(e.message); }
  }, [path, status, q]);
  useEffect(() => { if (user && STAFF.includes(user.role)) load(); }, [user, load]);

  const patch = async (id, body, quiet = false) => {
    try {
      const res = await fetch(`${API_URL}/api/staff/website-enquiries/${id}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Update failed');
      if (!quiet) { toast.success('Saved'); load(); }
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <div className="border-b sticky top-0 z-20 shadow-sm bg-[#27292E] text-white">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <Link to="/staff" className="text-white/80 hover:text-white" data-testid="website-enquiries-back"><ArrowLeft className="w-5 h-5" /></Link>
          <Link to="/" className="text-white/80 hover:text-white hidden sm:inline-flex"><Home className="w-5 h-5" /></Link>
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center"><Inbox className="w-4 h-4" /></div>
          <h1 className="text-base sm:text-lg font-bold truncate flex-1">Website Enquiries &amp; Contacts</h1>
          {summary && <span className="text-xs text-white/80" data-testid="website-enquiries-summary">{summary.new} new · {summary.total} total</span>}
        </div>
        <div className="max-w-5xl mx-auto px-3 sm:px-4 flex gap-1">
          {[['contacts', 'Contacts (all forms)'], ['submissions', 'Form submissions']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setParams({ view: k })} aria-selected={view === k} data-testid={`we-view-${k}`}
              className={`px-3 py-2 text-sm font-semibold border-b-2 ${view === k ? 'border-amber-400 text-white' : 'border-transparent text-white/60 hover:text-white'}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {view === 'contacts' && <ContactsTab />}
        {view === 'submissions' && <>
        <Card className="p-3 flex flex-wrap items-center gap-2">
          {[['installer_trade', 'Trade account'], ['training', 'Training form'], ['all', 'All forms']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setPath(k)} aria-pressed={path === k} className={`text-xs font-semibold px-3 py-1 rounded-full border ${path === k ? 'bg-[#27292E] text-white border-[#27292E]' : 'bg-white text-gray-600 border-gray-200'}`} data-testid={`enquiry-path-${k}`}>{l}</button>
          ))}
          <span className="w-px h-5 bg-gray-200 mx-1" />
          {[...STATUS.map(([k, l]) => [k, l]), ['all', 'All']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setStatus(k)} aria-pressed={status === k} className={`text-xs font-semibold px-3 py-1 rounded-full border ${status === k ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-600 border-gray-200'}`} data-testid={`enquiry-filter-${k}`}>
              {l}{summary && k !== 'all' ? ` (${summary[k] || 0})` : ''}
            </button>
          ))}
          <div className="relative ml-auto min-w-[200px]">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company…" className="h-8 pl-7 text-xs bg-white" data-testid="enquiry-search" />
          </div>
        </Card>
        {!items && <div className="py-10 flex justify-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>}
        {items && items.length === 0 && <Card className="p-10 text-center text-sm text-gray-500" data-testid="enquiries-empty">Nothing here. New website form submissions will appear the moment they arrive.</Card>}
        {items && items.map((e) => <EnquiryCard key={e.id} e={e} onPatch={patch} />)}
        </>}
      </div>
    </div>
  );
}
